/**
 * The dialog shell every OVS percentage editor opens into: header, the grid,
 * and an explicit **Save changes** footer.
 *
 * One component rather than three near-identical dialogs (campus split,
 * Straight Serve milk split, district defaults) so the save affordance, the
 * pending-count line, and the discard guard can never drift apart between
 * them — three copies of a Save button is how one of them ends up not having
 * one.
 *
 * ## The discard guard
 *
 * Closing with unsaved edits asks before throwing them away. The bug report
 * that started this work was numbers vanishing; a dialog that silently drops
 * a typed percentage on an outside-click is the same failure wearing a
 * different hat.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ordo/ui/alert-dialog'
import { Button } from '@ordo/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ordo/ui/dialog'
import { LoadingButton } from '@ordo/ui/loading-button'
import { type ReactNode, useState } from 'react'

import type { SplitEditor } from './split-editor'

export function SplitEditDialog({
  open,
  onClose,
  title,
  description,
  editor,
  children,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  description: ReactNode
  editor: SplitEditor
  children: ReactNode
}) {
  const [confirmDiscard, setConfirmDiscard] = useState(false)

  function requestClose() {
    if (editor.dirty) {
      setConfirmDiscard(true)
      return
    }
    editor.discard()
    onClose()
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            requestClose()
          }
        }}>
        {open ? (
          <DialogContent className="sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </DialogHeader>
            <DialogBody>{children}</DialogBody>
            <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p aria-live="polite" className="text-xs text-muted-foreground">
                {editor.invalid
                  ? 'Percentages must be between 0 and 100.'
                  : editor.pendingCount > 0
                    ? `${editor.pendingCount} unsaved change${editor.pendingCount === 1 ? '' : 's'}`
                    : 'No unsaved changes'}
              </p>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="ghost" onClick={requestClose} disabled={editor.saving}>
                  Cancel
                </Button>
                <LoadingButton
                  type="button"
                  loading={editor.saving}
                  disabled={!editor.dirty || editor.invalid}
                  onClick={() => {
                    void editor.save().then((ok) => {
                      if (ok) {
                        onClose()
                      }
                    })
                  }}>
                  Save changes
                </LoadingButton>
              </div>
            </DialogFooter>
          </DialogContent>
        ) : null}
      </Dialog>

      <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved percentages?</AlertDialogTitle>
            <AlertDialogDescription>
              {editor.pendingCount} change{editor.pendingCount === 1 ? '' : 's'} hasn&rsquo;t been saved. Closing now
              throws {editor.pendingCount === 1 ? 'it' : 'them'} away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                editor.discard()
                setConfirmDiscard(false)
                onClose()
              }}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
